import React, { useState, useEffect, useMemo } from 'react';
import { Paper, TextField, Button, Typography, Box, Alert, Divider, Container, FormControlLabel, Checkbox, Link, LinearProgress, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../utils/axios';  // 修改导入
import { usePageTranslation } from '../i18n/hooks';

// Password policy: min 8 chars, uppercase, lowercase, number, special char
const checkPasswordPolicy = (password) => ({
  length: (password || '').length >= 8,
  uppercase: /[A-Z]/.test(password || ''),
  lowercase: /[a-z]/.test(password || ''),
  number: /[0-9]/.test(password || ''),
  special: /[^A-Za-z0-9]/.test(password || ''),
});
const passwordPolicyPassed = (password) =>
  Object.values(checkPasswordPolicy(password)).every(Boolean);

const Register = () => {
  const { t } = usePageTranslation('register');
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [inviteCode, setInviteCode] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [errors, setErrors] = useState({});
  const [registrationStatus, setRegistrationStatus] = useState({
    success: false,
    message: '',
    emailSent: false
  });
  const navigate = useNavigate();
  const location = useLocation();

  // Build the login path, preserving a ?redirect= param so flows like team
  // join can resume after the user verifies their email and signs in.
  const loginPath = () => {
    const redirectTo = new URLSearchParams(location.search).get('redirect');
    return redirectTo ? `/login?redirect=${encodeURIComponent(redirectTo)}` : '/login';
  };

  // Live password policy state
  const policy = useMemo(() => checkPasswordPolicy(formData.password), [formData.password]);
  const policyScore = Object.values(policy).filter(Boolean).length; // 0..5
  const strengthColor =
    policyScore <= 2 ? 'error' : policyScore <= 3 ? 'warning' : policyScore <= 4 ? 'info' : 'success';

  // Auto-fill invite code from ?invite= URL query param (e.g. from an
  // admin-issued invitation link).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const invite = params.get('invite');
    if (invite) {
      setInviteCode(invite);
    }
  }, [location.search]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    // Clear specific field error when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateForm = () => {
    const errors = {};
    
    // Username validation
    if (!formData.username.trim()) {
      errors.username = t('auth.validation.usernameRequired');
    } else if (formData.username.trim().length < 3) {
      errors.username = t('auth.validation.usernameTooShort');
    }
    
    // Email validation
    if (!formData.email.trim()) {
      errors.email = t('auth.validation.emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = t('auth.validation.emailInvalid');
    }
    
    // Password validation
    if (!formData.password) {
      errors.password = t('auth.validation.passwordRequired');
    } else if (!passwordPolicyPassed(formData.password)) {
      errors.password = t('auth.validation.passwordWeak');
    }
    
    // Confirm password validation
    if (!formData.confirmPassword) {
      errors.confirmPassword = t('auth.validation.confirmPasswordRequired');
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = t('auth.messages.passwordMismatch');
    }

    // Invite code validation — registration is invite-only
    if (!inviteCode.trim()) {
      errors.inviteCode = t('auth.validation.inviteCodeRequired');
    }

    // Terms acceptance validation
    if (!termsAccepted) {
      errors.terms = t('auth.terms.required');
    }
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      const payload = {
        username: formData.username,
        password: formData.password,
        email: formData.email,
        invite_code: inviteCode.trim(),
      };
      const response = await axiosInstance.post('/api/register', payload);

      const data = response.data;

      setRegistrationStatus({
        success: true,
        message: data.message,
        emailSent: data.email_sent
      });
      
      // Clear any previous errors
      setErrors({});
      
      // 修改这里：导航到 login 页面（保留 redirect 以便恢复流程，如团队加入）
      if (!data.email_sent) {
        setTimeout(() => navigate(loginPath()), 5000);
      }
    } catch (error) {
      setErrors({ 
        submit: error.response?.data?.detail || 
                error.response?.data?.message || 
                t('auth.messages.registrationFailed')
      });
    }
  };


  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Paper 
        sx={{ 
          p: 4,
          bgcolor: '#FFFFFF',
          backdropFilter: 'none',
          border: '1px solid #E5E7EB',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}
      >
        <Typography variant="h1" component="h1" gutterBottom align="center" sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
          {t('title')}
        </Typography>
        <Typography variant="h6" color="text.secondary" gutterBottom align="center" sx={{ mb: 3 }}>
          {t('subtitle')}
        </Typography>
        
        {registrationStatus.success && (
          <Alert 
            severity={registrationStatus.emailSent ? "success" : "warning"} 
            sx={{ mb: 2 }}
          >
            {registrationStatus.message}
            {registrationStatus.emailSent && (
              <>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {t('auth.messages.checkEmail')}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                  {t('auth.messages.checkSpam')}
                </Typography>
              </>
            )}
          </Alert>
        )}
        
        {errors.submit && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.submit}
          </Alert>
        )}
        
        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            required
            fullWidth
            margin="normal"
            label={t('auth.fields.username')}
            name="username"
            value={formData.username}
            onChange={handleChange}
            error={!!errors.username}
            helperText={errors.username}
            autoComplete="username"
          />
          
          <TextField
            required
            fullWidth
            margin="normal"
            label={t('auth.fields.email')}
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
            autoComplete="email"
          />
          
          <TextField
            required
            fullWidth
            margin="normal"
            label={t('auth.fields.password')}
            type="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            error={!!errors.password}
            helperText={errors.password}
            autoComplete="new-password"
          />

          {/* Password policy requirements */}
          <Box sx={{ mt: 1, mb: 1, p: 1.5, border: '1px solid #E5E7EB', borderRadius: 1, bgcolor: '#FAFAFA' }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              {t('auth.passwordPolicy.title')}
            </Typography>
            {formData.password && (
              <Box sx={{ mt: 0.75, mb: 0.5 }}>
                <LinearProgress
                  variant="determinate"
                  value={(policyScore / 5) * 100}
                  color={strengthColor}
                  sx={{ height: 6, borderRadius: 3 }}
                />
                <Typography variant="caption" color={`${strengthColor}.main`} sx={{ fontWeight: 600 }}>
                  {t(`auth.passwordPolicy.strength.${strengthColor === 'error' ? 'weak' : strengthColor === 'warning' ? 'fair' : strengthColor === 'info' ? 'good' : 'strong'}`)}
                </Typography>
              </Box>
            )}
            <List dense sx={{ py: 0 }}>
              {[
                { key: 'length', met: policy.length },
                { key: 'uppercase', met: policy.uppercase },
                { key: 'lowercase', met: policy.lowercase },
                { key: 'number', met: policy.number },
                { key: 'special', met: policy.special },
              ].map(({ key, met }) => (
                <ListItem key={key} sx={{ py: 0, minHeight: 26, transition: 'all 0.2s ease' }}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    {met
                      ? <CheckCircleIcon fontSize="small" sx={{ color: 'success.main', transition: 'transform 0.2s ease', transform: 'scale(1)' }} />
                      : <RadioButtonUncheckedIcon fontSize="small" sx={{ color: 'text.disabled' }} />}
                  </ListItemIcon>
                  <ListItemText
                    primary={t(`auth.passwordPolicy.rules.${key}`)}
                    primaryTypographyProps={{
                      variant: 'caption',
                      sx: {
                        color: met ? 'success.main' : 'text.secondary',
                        textDecoration: met ? 'line-through' : 'none',
                        textDecorationColor: met ? 'rgba(46, 125, 50, 0.5)' : 'transparent',
                        transition: 'color 0.2s ease, text-decoration-color 0.2s ease',
                      },
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>

          <TextField
            required
            fullWidth
            margin="normal"
            label={t('auth.fields.confirmPassword')}
            type="password"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            autoComplete="new-password"
          />

          <TextField
            required
            fullWidth
            margin="normal"
            label={t('auth.fields.inviteCode')}
            name="inviteCode"
            value={inviteCode}
            onChange={(e) => {
              setInviteCode(e.target.value);
              if (errors.inviteCode) {
                setErrors({ ...errors, inviteCode: '' });
              }
            }}
            error={!!errors.inviteCode}
            helperText={errors.inviteCode || t('auth.fields.inviteCodeHelp')}
            inputProps={{ style: { letterSpacing: '0.1em' } }}
          />
          
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox 
                  checked={termsAccepted}
                  onChange={(e) => {
                    setTermsAccepted(e.target.checked);
                    if (errors.terms) {
                      setErrors({ ...errors, terms: '' });
                    }
                  }}
                  name="termsAccepted"
                />
              }
              label={
                <Typography variant="body2">
                  {t('auth.terms.label')}{' '}
                  <Link 
                    href="/terms" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    sx={{ textDecoration: 'underline' }}
                  >
                    {t('auth.terms.link')}
                  </Link>
                </Typography>
              }
            />
            {errors.terms && (
              <Typography variant="caption" color="error" sx={{ ml: 2, display: 'block' }}>
                {errors.terms}
              </Typography>
            )}
          </Box>
          
          <Button 
            type="submit" 
            fullWidth 
            variant="contained" 
            size="large"
            disabled={!termsAccepted || !passwordPolicyPassed(formData.password)}
            sx={{ mt: 3, mb: 2 }}
          >
            {t('auth.actions.register')}
          </Button>

          <Divider sx={{ my: 3 }} />
          
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('auth.actions.alreadyHaveAccount')}
              <Button 
                variant="text" 
                onClick={() => navigate(loginPath())}
                sx={{ ml: 1 }}
              >
                {t('auth.actions.signIn')}
              </Button>
            </Typography>
          </Box>
          
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ 
              mt: 2,
              backgroundColor: '#FFFFFF',
              border: '1px solid #E5E7EB',
              p: 2,
              borderRadius: 1,
              fontSize: '0.875rem',
              lineHeight: 1.6
            }}
          >
            {t('auth.messages.verificationNote')}
          </Typography>

          {/* Resend Verification Section */}
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('auth.messages.didNotReceiveEmail')}
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate('/resend-verification', { state: { email: formData.email } })}
              sx={{ minWidth: 200 }}
            >
              {t('auth.actions.resendVerification')}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default Register;